/**
 * Tracks which documents have been downloaded by the portal contact.
 * Uses the coreBOS KV store with key: `digibox_downloads_{contactId}`
 * Value: JSON object { [recordId]: { downloadedAt: ISO string, filename: string } }
 */

import { useState, useEffect, useCallback } from "react";
import { usePortalContact } from "./usePortalContact";

interface DownloadRecord {
  downloadedAt: string;
  filename?: string;
}

type DownloadMap = Record<string, DownloadRecord>;

const CACHE_KEY = "digibox_downloads_cache";

export function useDownloadTracker() {
  const { contactId } = usePortalContact();
  const [downloads, setDownloads] = useState<DownloadMap>(() => {
    try {
      return JSON.parse(localStorage.getItem(CACHE_KEY) || "{}");
    } catch {
      return {};
    }
  });
  const [loaded, setLoaded] = useState(false);

  const kvKey = contactId ? `digibox_downloads_${contactId}` : null;

  // Load from KV store on mount
  useEffect(() => {
    if (!kvKey) { setLoaded(true); return; }
    const load = async () => {
      try {
        const res = await fetch(`/api/kv/${kvKey}`, { credentials: "include" });
        const data = await res.json();
        if (data.success && data.value) {
          const parsed = JSON.parse(data.value);
          setDownloads(parsed);
          localStorage.setItem(CACHE_KEY, data.value);
        }
      } catch {
        // use cache
      }
      setLoaded(true);
    };
    load();
  }, [kvKey]);

  // Save to KV store
  const persist = useCallback(async (map: DownloadMap) => {
    if (!kvKey) return;
    const value = JSON.stringify(map);
    localStorage.setItem(CACHE_KEY, value);
    try {
      await fetch(`/api/kv/${kvKey}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ value }),
      });
    } catch {
      // offline — cached locally
    }
  }, [kvKey]);

  const markDownloaded = useCallback((recordId: string, filename?: string) => {
    setDownloads((prev) => {
      const next = {
        ...prev,
        [recordId]: { downloadedAt: new Date().toISOString(), filename },
      };
      persist(next);
      return next;
    });
  }, [persist]);

  const isDownloaded = useCallback((recordId: string): boolean => {
    return !!downloads[recordId];
  }, [downloads]);

  const getDownloadInfo = useCallback((recordId: string): DownloadRecord | null => {
    return downloads[recordId] || null;
  }, [downloads]);

  return { downloads, markDownloaded, isDownloaded, getDownloadInfo, loaded };
}
